/**
 * Buyer favorites: shop.follows
 *
 * The buyer (operator user) follows vendors through their active org.
 * RLS blocks the insert if the buyer's org does not have the `shop`
 * module enabled in core.org_module_flags.
 */

import { supabase } from '../client';
import { fireShopAdConversion } from './shopAdsV2';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type { ShopFollow, ShopVendorPublic } from './shopTypes';

export interface FollowedVendor extends ShopVendorPublic {
  followed_at: string;
}

export interface ListFollowsInput extends KeysetInput {
  /** When set, restrict to follows under this operator org. */
  buyerOrgId?: string;
}

/** Returns the calling user's follows (own rows only via RLS). */
export async function listShopFollows(
  input: ListFollowsInput = {},
): Promise<ListResult<ShopFollow>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('follows')
    .select('*', selectCountOption(input.countMode))
    .is('archived_at', null);
  if (input.buyerOrgId) q = q.eq('buyer_org_id', input.buyerOrgId);
  q = q.order('created_at', { ascending: false }).order('vendor_id', { ascending: false });
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopFollow[];
  // shop.follows does not have updated_at; we paginate via created_at + vendor_id manually.
  let next: { sortValue: string; id: string } | null = null;
  if (rows.length === pageSize) {
    const last = rows[rows.length - 1]!;
    next = { sortValue: last.created_at, id: last.vendor_id };
  }
  return {
    rows,
    nextCursor: next,
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * Returns the calling user's followed vendors joined onto the public
 * vendor view. Two-step approach to keep the join simple under RLS.
 */
export async function listFollowedVendors(
  input: ListFollowsInput = {},
): Promise<ListResult<FollowedVendor>> {
  const pageSize = clampPageSize(input.pageSize);
  const { rows: follows, total, totalIsExact, nextCursor } = await listShopFollows({
    ...input,
    pageSize,
  });
  if (follows.length === 0) {
    return { rows: [], total, totalIsExact, nextCursor };
  }
  const ids = follows.map((f) => f.vendor_id);
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('v_vendor_public')
    .select('*')
    .in('id', ids)
    .limit(pageSize);
  if (error) throw error;
  const vendorsById = new Map<string, ShopVendorPublic>();
  for (const v of (data ?? []) as ShopVendorPublic[]) vendorsById.set(v.id, v);
  const rows: FollowedVendor[] = follows
    .map((f) => {
      const v = vendorsById.get(f.vendor_id);
      if (!v) return null;
      return { ...v, followed_at: f.created_at };
    })
    .filter((x): x is FollowedVendor => x !== null);
  return { rows, total, totalIsExact, nextCursor };
}

export async function isFollowing(
  vendorId: string,
  buyerOrgId: string,
): Promise<boolean> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('follows')
    .select('vendor_id')
    .eq('vendor_id', vendorId)
    .eq('buyer_org_id', buyerOrgId)
    .is('archived_at', null)
    .maybeSingle();
  if (error) throw error;
  return data !== null && data !== undefined;
}

export async function followShopVendor(input: {
  vendorId: string;
  buyerOrgId: string;
}): Promise<ShopFollow> {
  // Capture the user id from the session. RLS enforces user_id=auth.uid().
  const { data: userData, error: ue } = await supabase().auth.getUser();
  if (ue) throw ue;
  const userId = userData.user?.id;
  if (!userId) throw new Error('Sign in to follow a vendor.');

  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('follows')
    .upsert(
      {
        user_id: userId,
        vendor_id: input.vendorId,
        buyer_org_id: input.buyerOrgId,
        archived_at: null,
      },
      { onConflict: 'user_id,vendor_id' },
    )
    .select('*')
    .single();
  if (error) throw error;
  // shop.follows has no surrogate id (composite primary key on
  // user_id, vendor_id). The attribution row uses vendor_id as the
  // target so a single user re-following the same vendor only
  // attributes once.
  void fireShopAdConversion({
    eventType: 'conversion_follow',
    targetKind: 'follow',
    targetId: input.vendorId,
  });
  return data as ShopFollow;
}

export async function unfollowShopVendor(input: {
  vendorId: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('follows')
    .update({ archived_at: new Date().toISOString() })
    .eq('vendor_id', input.vendorId);
  if (error) throw error;
}

export async function getShopVendorFollowerCount(vendorId: string): Promise<number> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_get_follower_count', { p_vendor_id: vendorId });
  if (error) throw error;
  return typeof data === 'number' ? data : Number(data ?? 0);
}
